import { useCallback, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { LoginPage } from './features/auth/LoginPage'
import { ChangePasswordPage } from './features/auth/ChangePasswordPage'
import { AccountListPage } from './features/accounts/AccountListPage'
import { TenantListPage } from './features/platform/TenantListPage'
import { AdministratorListPage } from './features/platform/AdministratorListPage'
import { PolicyBuilderPage } from './features/policies/PolicyBuilderPage'
import { PolicyEditorPage } from './features/policies/PolicyEditorPage'
import { PolicyGroupPage } from './features/policies/PolicyGroupPage'
import { EventDetailPage } from './features/events/EventDetailPage'
import { DeviceDetailPage } from './features/devices/DeviceDetailPage'
import { DictionaryEditorPage } from './features/dictionaries/DictionaryEditorPage'
import { AlertDetailPage } from './features/alerts/AlertDetailPage'
import { SystemPage } from './features/system/SystemPage'
import { DashboardPage } from './features/explore/DashboardPage'
import { ExportPage } from './features/export/ExportPage'
import { SettingsPage } from './features/settings/SettingsPage'
import { Shell } from './components/Shell'
import { CasesPage } from './features/cases/CasesPage'
import { PoliciesPage } from './features/policies/PoliciesPage'
import { DevicesPage } from './features/devices/DevicesPage'
import { PeoplePage } from './features/people/PeoplePage'
import { BrandingPage } from './features/branding/BrandingPage'
import { LegacyRedirect } from './components/LegacyRedirect'
import { PARAMETERISED_REDIRECTS, STATIC_REDIRECTS } from './lib/redirects'
import { PreferencesProvider } from './lib/PreferencesContext'
import { session, type SignInResponse } from './lib/api'
import { canReach, homeRouteFor, navigationRoles } from './lib/navigation'
import { basenameFor, canonicalAddress, isOtherOrgSignIn, orgFor, routePath, type OrgIdentity } from './lib/orgPath'

/**
 * @param basename The organization segment the router was mounted under, or empty (`main.tsx`).
 */
export function App({ basename = '' }: { basename?: string }) {
  const [identity, setIdentity] = useState<SignInResponse | null>(() => session.identity())

  /**
   * The screen somebody asked for before they had signed in, so signing in can take them to it.
   *
   * Read once, from the address the page was loaded at. A link to `/fleettest/devices/7` pasted into
   * a ticket is the whole reason the organization is in the address, and it would be worth very
   * little if following it signed-out put somebody on the Board. Not the sign-in page itself and not
   * the bare root, neither of which is a destination.
   */
  const [requested, setRequested] = useState<string | null>(() => {
    if (session.identity()) return null
    const path = routePath(window.location.pathname)
    return path === '/' || path === '/sign-in' ? null : path + window.location.search
  })

  /**
   * Why the sign-in screen is showing, and where to go back to afterwards.
   *
   * Held here rather than in router state, which is where this first put it and where it did not
   * survive: the moment the identity goes null the signed-out branch below renders, its catch-all
   * route redirects to `/sign-in`, and that redirect replaces the entry carrying the state. Two
   * things were navigating for the same reason and the one with nothing to say won.
   */
  const [ended, setEnded] = useState<{ reason: 'session-expired' | 'no-session'; from: string } | null>(null)

  /**
   * Where the next sign-in should land, decided by `signIn` and carried out by the catch-all below.
   *
   * Declarative because imperative navigation cannot win here, which took a browser to see. Signing
   * in sets the identity and navigates in one handler, but the router's location settles before the
   * identity does - so the signed-out tree renders at the new route, its catch-all sends it to
   * `/sign-in`, and the signed-in tree then finds `/sign-in` unmatched and sends it home. Two
   * redirects beat one `navigate`, and somebody who was reading a device ended up on the Board.
   *
   * Letting the catch-all carry the destination makes it one navigation instead of three, and there
   * is no order for it to get wrong.
   */
  const [landing, setLanding] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  /**
   * Goes to a route as a particular identity, reloading when that identity lives under another
   * organization segment.
   *
   * A reload because the router's basename is fixed for the life of the page, and navigating to
   * `/board` under the wrong one would show a customer's screens at another customer's address. The
   * session is in local storage and survives it. Returns whether it reloaded, so a caller knows not
   * to go on updating state the page is about to discard.
   */
  const goTo = useCallback(
    (who: OrgIdentity, path: string, replace = false): boolean => {
      const target = basenameFor(orgFor(who))
      if (target === basename) {
        navigate(path, { replace })
        return false
      }
      if (replace) window.location.replace(target + path)
      else window.location.assign(target + path)
      return true
    },
    [basename, navigate]
  )

  /**
   * What the console does when the session changes underneath it.
   *
   * This is the half of the expiry defect somebody actually felt. `request` cleared the token on a
   * 401 and told nobody, and the identity below is seeded once - so the console kept rendering as
   * though signed in, with no token, and every screen showed its own error until the page was
   * reloaded by hand. Setting the identity to null here flips the gate further down and the whole
   * console unmounts in one commit, which is what a session ending should look like.
   */
  useEffect(
    () =>
      session.subscribe((event) => {
        if (event.kind === 'renewed') {
          setIdentity(event.identity)
          return
        }

        // A support session ran out. The platform session was parked, not discarded (D-299), so the
        // administrator goes back to their own rather than to the sign-in page.
        if (event.kind === 'released') {
          if (!goTo(event.identity, '/platform/tenants', true)) setIdentity(event.identity)
          return
        }

        // No navigation here on purpose. Setting the identity to null flips the gate below, whose
        // catch-all route does the redirect - and doing both means two navigations racing, which is
        // how the reason was lost. This only has to say what happened.
        setEnded({ reason: event.reason, from: event.at })
        setIdentity(null)
      }),
    [goTo]
  )

  /**
   * Forgets the landing route once somebody has actually arrived on it.
   *
   * Keyed on the location matching, not merely on the redirect having been issued, and the
   * difference is the whole bug. Clearing as soon as the catch-all had consumed it produced a render
   * where `landing` was already gone and the router's location had not caught up - so the catch-all
   * rendered again, found nothing to land on, and sent somebody to the Board one frame after
   * putting them where they asked to be. The history showed `/devices` and then `/board`.
   *
   * Clearing at all matters because the route belongs to the sign-in that chose it: left set, a
   * mistyped URL an hour later would return somebody to wherever their last session happened to end.
   */
  useEffect(() => {
    if (landing && location.pathname + location.search === landing) setLanding(null)
  }, [landing, location])

  const signIn = useCallback(
    (next: SignInResponse) => {
      // Back to whatever the ended session was in the middle of, when there was one and these roles
      // can reach it. The role check is not ceremony: somebody whose session died on /devices and
      // who then signs in as a platform administrator must not land on a fleet screen, because the
      // platform organization has no fleet.
      //
      // Otherwise it depends on what they are, for the same reason: sending a platform administrator
      // to Policies would open a screen about a fleet they do not have.
      const from = ended?.from ?? requested
      const destination = from && canReach(from, next.roles) ? from : homeRouteFor(next.roles)

      // Another organization's segment, or none where there was one: the page reloads under the
      // right basename and lands directly, so none of the state below would outlive it. Signing in
      // over a live session - somebody at an MSP opening a second customer's sign-in link - replaces
      // that session outright, parked platform session included, rather than leaving it to be
      // restored later under an organization nobody chose.
      if (basenameFor(orgFor(next)) !== basename) {
        if (identity) session.clearAll()
        session.save(next)
        window.location.assign(basenameFor(orgFor(next)) + destination)
        return
      }

      session.save(next)
      setEnded(null)
      setRequested(null)
      setLanding(destination)
      setIdentity(next)
    },
    [basename, ended, identity, requested]
  )

  const signOut = useCallback(() => {
    // Abandons the support session and the platform one together: signing out means signing out.
    session.clearAll()
    // Cleared, because somebody who chose to sign out is not somebody whose session ran out, and
    // telling them it did would be a small lie on the way out.
    setEnded(null)
    setRequested(null)
    setLanding(null)
    setIdentity(null)
    navigate('/sign-in')
  }, [navigate])

  /** A platform administrator has just opened a customer organization. */
  const onAssumed = useCallback(() => {
    const assumed = session.identity()
    if (assumed && goTo(assumed, homeRouteFor(assumed.roles))) return
    setIdentity(assumed)
    navigate(homeRouteFor(assumed?.roles ?? []))
  }, [goTo, navigate])

  /**
   * A support session was extended in place.
   *
   * Deliberately not `onAssumed`, which navigates home: an administrator extending a session is in
   * the middle of looking at something, and moving them off it is the interruption this feature
   * exists to remove.
   */
  const onExtended = useCallback(() => setIdentity(session.identity()), [])

  /** They are handing it back. The platform session was parked, not discarded. */
  const releaseTenant = useCallback(() => {
    const restored = session.release()
    if (restored && goTo(restored, '/platform/tenants')) return
    setIdentity(restored)
    navigate(restored ? '/platform/tenants' : '/sign-in')
  }, [goTo, navigate])

  /**
   * The forced password change is over. Re-read rather than patched, so the console's idea of the
   * session comes from one place.
   */
  const onPasswordChanged = useCallback(() => {
    const current = session.identity()
    if (current) {
      const updated = { ...current, mustChangePassword: false, passwordExpired: false }
      session.save(updated)
      setIdentity(updated)
      navigate(homeRouteFor(updated.roles))
      return
    }
    navigate('/sign-in')
  }, [navigate])

  if (!identity) {
    return (
      <Routes>
        <Route
          path="/sign-in"
          element={<LoginPage onSignedIn={signIn} ended={ended?.reason === 'session-expired'} />}
        />
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    )
  }

  // Another organization's sign-in link, opened while signed in to this one. Shown rather than
  // redirected, because following that link is how somebody with accounts in several organizations
  // moves between them; `signIn` replaces the session and reloads under the new segment.
  if (isOtherOrgSignIn(identity, window.location.pathname)) {
    return <LoginPage onSignedIn={signIn} />
  }

  // The address names no organization, the wrong one, or the right one in the wrong case. Replaced
  // rather than pushed, so Back does not return to an address that would only redirect again.
  const canonical = canonicalAddress(identity, window.location.pathname, window.location.search, window.location.hash)
  if (canonical) return <Relocate to={canonical} />

  // Rendered instead of the console rather than in front of it, so there is nothing behind it to
  // navigate back to. The server refuses every other endpoint anyway; this is what stops somebody
  // discovering that one 403 at a time.
  //
  // Two reasons reach this screen and it says which. Somebody whose password has simply aged out has
  // not been handed anything by anybody, and telling them otherwise would be a small accusation.
  if (identity.mustChangePassword || identity.passwordExpired) {
    return (
      <ChangePasswordPage
        reason={identity.mustChangePassword ? 'handed-over' : 'expired'}
        onChanged={onPasswordChanged}
      />
    )
  }

  return (
    <PreferencesProvider>
      <Routes>
        {/*
          One layout route, so the shell mounts once and the screens render into its outlet. It used
          to be twenty-two flat routes with the masthead re-rendered beside each; that worked while
          the chrome was a single bar, and does not once the chrome holds a coverage read, a query bar
          and an inspector that must survive a navigation between rows.
        */}
        <Route
          element={
            <Shell
              displayName={identity.displayName}
              roles={navigationRoles(identity)}
              tenantName={identity.tenantName}
              tenantSlug={identity.tenantSlug}
              assumedByName={identity.assumedByName}
              assumedExpiresUtc={identity.expiresUtc}
              onSignOut={signOut}
              onReleaseTenant={releaseTenant}
              onExtended={onExtended}
            />
          }
        >
          <Route path="/board" element={<DashboardPage />} />
          <Route path="/cases" element={<CasesPage />} />
          <Route path="/cases/:alertId" element={<AlertDetailPage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          <Route path="/policies/build" element={<PolicyBuilderPage />} />
          <Route path="/policies/groups/:groupId" element={<PolicyGroupPage />} />
          <Route path="/policies/new" element={<PolicyEditorPage />} />
          <Route path="/policies/:policyId" element={<PolicyEditorPage />} />
          <Route path="/dictionaries/:dictionaryId" element={<DictionaryEditorPage />} />
          <Route path="/devices" element={<DevicesPage />} />
          <Route path="/devices/:deviceId" element={<DeviceDetailPage />} />
          <Route path="/events/:eventRecordId" element={<EventDetailPage />} />
          <Route path="/system" element={<SystemPage />} />
          <Route path="/export" element={<ExportPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/accounts" element={<AccountListPage />} />
          <Route path="/branding" element={<BrandingPage />} />
          <Route path="/platform/tenants" element={<TenantListPage onAssumed={onAssumed} />} />
          <Route path="/platform/administrators" element={<AdministratorListPage />} />

          {/*
            Every path the console used to have still works. Bookmarks exist, the end-to-end suite
            navigates by URL throughout, and the screenshots in server/screenshots are named after the
            old routes - so these are kept indefinitely rather than for a deprecation window. Driven
            from lib/redirects.ts, which layout.spec.ts also drives, so a redirect cannot exist in the
            router and be missing from the gate.
          */}
          {STATIC_REDIRECTS.map((route) => (
            <Route key={route.from} path={route.from} element={<Navigate to={route.to} replace />} />
          ))}
          {PARAMETERISED_REDIRECTS.map((route) => (
            <Route key={route.from} path={route.from} element={<LegacyRedirect to={route.to} />} />
          ))}

          <Route path="*" element={<Navigate to={landing ?? homeRouteFor(identity.roles)} replace />} />
        </Route>
      </Routes>
    </PreferencesProvider>
  )
}

/**
 * Replaces the page with another address, from an effect rather than during render.
 *
 * A full load rather than a router navigation, for the reason `goTo` gives: the address differs in
 * its organization segment, and that is the router's basename.
 */
function Relocate({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to)
  }, [to])
  return null
}
